import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import './ReinaRank.css';

// ── Supabase ──────────────────────────────────────────────────────────────────
const supabase = (() => {
  const url  = process.env.REACT_APP_SUPABASE_URL;
  const key  = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return url && key ? createClient(url, key) : null;
})();

// ── Necklace data ─────────────────────────────────────────────────────────────
const NECKLACES = [
  {
    id: 'n1',
    imageFile: 'david-yurman-petite-infinity-pendant-silver.jpg',
    brand: 'David Yurman',
    name: 'Petite Infinity Pendant Necklace',
    material: 'Sterling Silver with Diamonds, 8mm diameter pendant',
    detail: 'Adjustable length, 38 cm – 43 cm',
  },
  {
    id: 'n2',
    imageFile: 'david-yurman-petite-infinity-pendant-gold.jpg',
    brand: 'David Yurman',
    name: 'Petite Infinity Pendant Necklace',
    material: '18K Yellow Gold with Diamonds, 8mm diameter pendant',
    detail: 'Adjustable length, 38 cm – 43 cm',
    active: false,
  },
  {
    id: 'n3',
    imageFile: 'david-yurman-petite-starburst-pendant-silver.jpg',
    brand: 'David Yurman',
    name: 'Petite Starburst Pendant Necklace',
    material: 'Sterling Silver with Diamonds, 10.5mm diameter pendant',
    detail: 'Adjustable length, 41 cm – 46 cm',
  },
  {
    id: 'n4',
    imageFile: 'david-yurman-petite-starburst-pendant-gold.jpg',
    brand: 'David Yurman',
    name: 'Petite Starburst Pendant Necklace',
    material: '18K Yellow Gold with Diamonds, 10.5mm diameter pendant',
    detail: 'Adjustable length, 41 cm – 46 cm',
    active: false,
  },
  {
    id: 'n5',
    imageFile: 'bulgari-divas-dream-mini-necklace.jpg',
    brand: 'Bulgari',
    name: "Divas' Dream Mini Necklace",
    material: '18K rose gold, set with mother-of-pearl element, 9mm pendant height',
    detail: 'Chain length: 37 cm – 40 cm',
  },
  {
    id: 'n6',
    imageFile: 'dior-etoils-des-vents.jpg',
    brand: 'Dior',
    name: 'Étoile des Vents Necklace',
    material: '18K pink gold with diamond, motif size S',
    detail: 'Length: 42 cm, adjustable to 38 cm or 40 cm',
    active: false,
  },
  {
    id: 'n7',
    imageFile: 'saint-laurent-necklace.jpg',
    brand: 'Saint Laurent',
    name: 'YSL Gold-Tone Necklace',
    material: 'Gold-tone metal, twisted circular pendant, 1.4cm pendant length',
    detail: 'Length: adjustable to your preferred drop',
  },
  {
    id: 'n8',
    imageFile: 'cartier-damour-pendant-pink.jpg',
    brand: 'Cartier',
    name: "D'Amour Pendant, Diamond, Small Model",
    material: '18K rose gold with diamond, 4.5mm pattern diameter',
    detail: 'Chain length: 38 cm – 41 cm',
  },
  {
    id: 'n9',
    imageFile: 'frivole-mini-white.jpg',
    brand: 'Van Cleef & Arpels',
    name: 'Frivole Pendant, Mini Model',
    material: '18K white gold, Diamond, 11mm pendant diameter',
    detail: 'Chain length: 40 cm',
  },
  {
    id: 'n10',
    imageFile: 'diapason-white.jpg',
    brand: 'Swarovski',
    name: 'Diapason Necklace',
    material: 'Mixed cuts, White, Rhodium plated, 1.3cm × 14cm pendant',
    detail: 'Length: 38 cm – 45 cm',
    active: false,
  },
  {
    id: 'n11',
    imageFile: 'octagon-silver.jpg',
    brand: 'Swarovski',
    name: 'Octagon Necklace',
    material: 'Diamonds, Round shape, Sterling silver, 6mm pattern diameter',
    detail: 'Length: 40 cm – 43 cm',
    active: false,
  },
  {
    id: 'n12',
    imageFile: 'tiffany-mini-heart-blue.jpg',
    brand: 'Tiffany & Co.',
    name: 'Return to Tiffany Heart Pendant, Mini',
    material: 'Silver, Tiffany Blue with a Diamond, 13mm pendant diameter',
    detail: 'Length: 41 cm – 46 cm',
  },
  {
    id: 'n13',
    imageFile: 'tiffany-open-heart-silver.jpg',
    brand: 'Tiffany & Co.',
    name: 'Elsa Peretti Open Heart Pendant',
    material: 'Sterling Silver, 7mm pendant diameter',
    detail: 'Length: 41 cm',
  },
  {
    id: 'n14',
    imageFile: 'klenota-heart-rose-gold.jpg',
    brand: 'Klenota',
    name: 'Heart Pendant in Rose Gold',
    material: '14K rose gold, rolo style chain, 7mm pendant height',
    detail: 'Length: 40 cm',
  },
  {
    id: 'n15',
    imageFile: 'catbird-nocturne-star-silver.jpg',
    brand: 'Catbird',
    name: 'Nocturne Star Necklace',
    material: 'Sterling silver',
    detail: 'Length: 43 cm',
  },
];

// ── Shuffle ───────────────────────────────────────────────────────────────────
function shuffled(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── FindTop (tie-aware tournament to find #1) ─────────────────────────────────
// champions: all items tied for first so far
// candidates: remaining items to compare
// losers: items beaten by any champion

function initFindTop(items) {
  const s = shuffled(items);
  return {
    champions: [s[0]],
    candidates: s.slice(1),
    losers: [],
    comparisons: 0,
    totalItems: s.length,
    done: false,
  };
}

function getFindTopComp(state) {
  if (state.done || state.candidates.length === 0) return null;
  return { a: state.champions[0], b: state.candidates[0] };
}

function applyFindTopChoice(state, choice) {
  if (state.done) return state;
  const [head, ...rest] = state.candidates;
  let newChampions, newLosers;
  if (choice === 'a') {
    newChampions = state.champions;
    newLosers = [...state.losers, head];
  } else if (choice === 'b') {
    newChampions = [head];
    newLosers = [...state.losers, ...state.champions];
  } else {
    // tie
    newChampions = [...state.champions, head];
    newLosers = state.losers;
  }
  const done = rest.length === 0;
  return {
    ...state,
    champions: newChampions,
    candidates: rest,
    losers: newLosers,
    comparisons: state.comparisons + 1,
    done,
  };
}

// ── GroupSort (tie-aware binary insertion sort) ───────────────────────────────
// sorted: array of groups, each group is an array of tied items
// lo/hi: group index bounds for current binary search

function initGroupSort(items) {
  if (items.length === 0) return { sorted: [], current: null, remaining: [], lo: 0, hi: -1, comparisons: 0, done: true };
  if (items.length === 1) return { sorted: [[items[0]]], current: null, remaining: [], lo: 0, hi: 0, comparisons: 0, done: true };
  const s = shuffled(items);
  return {
    sorted: [[s[0]]],
    current: s[1],
    remaining: s.slice(2),
    lo: 0,
    hi: 0,
    comparisons: 0,
    done: false,
  };
}

function getGroupComp(state) {
  if (state.done || !state.current) return null;
  const mid = Math.floor((state.lo + state.hi) / 2);
  return { a: state.current, b: state.sorted[mid][0], mid };
}

function applyGroupChoice(state, choice) {
  if (state.done || !state.current) return state;
  const mid = Math.floor((state.lo + state.hi) / 2);
  let { lo, hi, sorted, current, remaining, comparisons } = state;
  comparisons++;

  if (choice === 'tie') {
    const newSorted = [
      ...sorted.slice(0, mid),
      [...sorted[mid], current],
      ...sorted.slice(mid + 1),
    ];
    if (remaining.length === 0) {
      return { ...state, sorted: newSorted, current: null, done: true, comparisons };
    }
    const [next, ...rest] = remaining;
    return { ...state, sorted: newSorted, current: next, remaining: rest, lo: 0, hi: newSorted.length - 1, comparisons };
  }

  let newLo = lo, newHi = hi;
  if (choice === 'a') {
    newHi = mid - 1;
  } else {
    newLo = mid + 1;
  }

  if (newLo > newHi) {
    const newSorted = [...sorted.slice(0, newLo), [current], ...sorted.slice(newLo)];
    if (remaining.length === 0) {
      return { ...state, sorted: newSorted, current: null, done: true, comparisons };
    }
    const [next, ...rest] = remaining;
    return { ...state, sorted: newSorted, current: next, remaining: rest, lo: 0, hi: newSorted.length - 1, comparisons };
  }
  return { ...state, lo: newLo, hi: newHi, comparisons };
}

function getGroupSortProgress(state) {
  if (state.done) return 1;
  const groupsPlaced = state.sorted.length - 1;
  return groupsPlaced / (state.sorted.length + state.remaining.length);
}

// ── Flatten ranked results ────────────────────────────────────────────────────
function flattenRanked(champions, sortState) {
  const champsSorted = [...champions].sort((a, b) => a.name.localeCompare(b.name));
  const result = champsSorted.map(c => ({ ...c, displayRank: 1 }));
  if (!sortState || !sortState.done) return result;

  let rank = 1 + champions.length;
  for (const group of sortState.sorted) {
    const alpha = [...group].sort((a, b) => a.name.localeCompare(b.name));
    for (const item of alpha) {
      result.push({ ...item, displayRank: rank });
    }
    rank += group.length;
  }
  return result;
}

// ── Geo + Supabase logging ────────────────────────────────────────────────────
async function fetchGeo() {
  try {
    const r = await fetch('https://ipapi.co/json/');
    if (!r.ok) return {};
    return await r.json();
  } catch {
    return {};
  }
}

async function logNecklaceResult({ type, champions, fullRanking, comparisons }) {
  if (!supabase) return;
  try {
    const geo = await fetchGeo();
    await supabase.from('necklace_rankings').insert({
      ip: geo.ip || null,
      city: geo.city || null,
      country: geo.country_name || null,
      ranking_type: type,
      top_picks: champions.map(c => c.name),
      full_ranking: fullRanking
        ? fullRanking.map(item => ({ rank: item.displayRank, name: item.name, brand: item.brand }))
        : null,
      comparisons,
    });
  } catch (e) {
    console.error('Failed to log necklace result:', e);
  }
}

// ── Image preloader hook ──────────────────────────────────────────────────────
const ACTIVE_NECKLACES = NECKLACES.filter(n => n.active !== false);

function useImagePreloader() {
  useEffect(() => {
    ACTIVE_NECKLACES.forEach(n => {
      const img = new window.Image();
      img.src = `/necklaces/${n.imageFile}`;
    });
  }, []);
}

// ── Necklace image display ────────────────────────────────────────────────────
function NecklaceImage({ item, height = 200 }) {
  return (
    <div style={{
      height,
      background: 'radial-gradient(ellipse 65% 60% at 50% 44%, #1e1c18 0%, #0d0b09 100%)',
      overflow: 'hidden',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px',
      boxSizing: 'border-box',
    }}>
      <img
        src={`/necklaces/${item.imageFile}`}
        alt={item.name}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
        }}
      />
    </div>
  );
}

// ── Float label ───────────────────────────────────────────────────────────────
function FloatLabel({ text, x, y, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 750);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className="reina-float" style={{ left: x - 12, top: y }}>
      {text}
    </div>
  );
}

// ── Sticky Note Modal ─────────────────────────────────────────────────────────
function StickyNoteModal({ onConfirm, onDismiss }) {
  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 24,
      }}
    >
      <div
        className="reina-pop"
        onClick={e => e.stopPropagation()}
        style={{
          background: '#faf6ec',
          border: '1px solid #e4d8bc',
          boxShadow: '3px 5px 24px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.5)',
          padding: '24px 26px 22px',
          maxWidth: 300,
          width: '100%',
          transform: 'rotate(1.8deg)',
          fontFamily: "'Raleway', sans-serif",
        }}
      >
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: '#8a6840',
          borderBottom: '1px solid #e4d8bc', paddingBottom: 10, marginBottom: 14,
        }}>
          Note
        </div>
        <p style={{
          fontSize: 13.5, lineHeight: 1.75, color: '#4a3c28',
          margin: '0 0 20px', fontWeight: 400,
        }}>
          so uhh i did some more shopping and added a few more here
        </p>
        <p style={{
          fontSize: 13.5, lineHeight: 1.75, color: '#4a3c28',
          margin: '0 0 20px', fontWeight: 400,
        }}>
          hopefully we're getting closer :)
        </p>
        <button
          className="reina-btn reina-btn-gold"
          onClick={onConfirm}
          style={{ width: '100%', padding: '13px 0', fontSize: 10 }}
        >
          Begin
        </button>
      </div>
    </div>
  );
}

// ── Intro Screen ──────────────────────────────────────────────────────────────
function IntroScreen({ onStart }) {
  const [showNote, setShowNote] = useState(false);

  return (
    <div style={{
      height: '100dvh',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '16px 20px',
      overflowY: 'auto',
      boxSizing: 'border-box',
    }}>
      <div style={{ maxWidth: 520, width: '100%' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(32px, 6vw, 52px)',
            fontWeight: 300,
            letterSpacing: '0.12em',
            color: '#e8e0d0',
            lineHeight: 1,
            marginBottom: 10,
          }}>
            THE RANKER
          </div>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(11px, 2vw, 13px)',
            fontWeight: 400,
            letterSpacing: '0.4em',
            color: '#c5a96a',
            textTransform: 'uppercase',
          }}>
            Let's Try This Again?
          </div>
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: '#2a2520' }} />
          <div style={{ color: '#3a3020', fontSize: 10, letterSpacing: '0.2em' }}>◇</div>
          <div style={{ flex: 1, height: 1, background: '#2a2520' }} />
        </div>

        {/* Instructions */}
        <div style={{
          background: 'rgba(15,13,10,0.7)',
          border: '1px solid #2a2520',
          padding: '20px 24px',
          marginBottom: 20,
        }}>
          <div style={{
            fontFamily: "'Raleway', sans-serif",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#c5a96a',
            marginBottom: 14,
          }}>
            How It Works
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              "You will be shown two pieces at a time.",
              "Select your preference, or mark them as a Tie.",
              "Your choices will be ranked after a few rounds.",
              "Once your top pick is found, you can stop or continue to get your full ranking.",
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{
                  width: 18, height: 18, border: '1px solid #3a3020',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 1,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 11, color: '#c5a96a',
                }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: 13, color: '#b0a490', lineHeight: 1.6, fontWeight: 400 }}>
                  {step}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Begin button — opens the sticky note */}
        <button
          className="reina-btn reina-btn-gold"
          onClick={() => setShowNote(true)}
          style={{ width: '100%', padding: '14px 0', fontSize: 11 }}
        >
          Begin
        </button>
      </div>

      {showNote && (
        <StickyNoteModal
          onConfirm={onStart}
          onDismiss={() => setShowNote(false)}
        />
      )}
    </div>
  );
}

// ── Compare Card ──────────────────────────────────────────────────────────────
function CompareCard({ item, side, cardState, onChoose }) {
  const isChosen   = cardState === 'chosen';
  const isRejected = cardState === 'rejected';
  const isTie      = cardState === 'tie';

  const cls = [
    'rr-card',
    cardState === 'idle' ? (side === 'a' ? 'rr-card-a' : 'rr-card-b') : '',
    isChosen   ? (side === 'a' ? 'reina-winner-a' : 'reina-winner-b') : '',
    isRejected ? (side === 'a' ? 'reina-loser-a'  : 'reina-loser-b')  : '',
    isTie      ? 'reina-tie-card' : '',
  ].join(' ');

  return (
    <div
      className={cls}
      onClick={() => cardState === 'idle' && onChoose()}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        borderColor: isChosen ? '#c5a96a' : undefined,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <NecklaceImage item={item} height={190} />

      {isChosen && (
        <div style={{
          position: 'absolute', top: 10, right: 10,
          background: 'rgba(197,169,106,0.9)', color: '#0a0a0a',
          fontSize: 9, fontWeight: 700, letterSpacing: '0.15em',
          padding: '3px 8px', textTransform: 'uppercase',
        }}>
          Selected
        </div>
      )}

      <div style={{ padding: '14px 14px 18px', background: '#0f0f0f', borderTop: '1px solid #1e1b16' }}>
        <div style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(13px, 2.5vw, 16px)',
          fontWeight: 400, color: '#e8e0d0',
          lineHeight: 1.3, marginBottom: 5,
        }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11, color: '#6a5e50', lineHeight: 1.5 }}>
          {item.material}
        </div>
        {item.detail && (
          <div style={{ fontSize: 10, color: '#4a3e30', marginTop: 3 }}>
            {item.detail}
          </div>
        )}
        {cardState === 'idle' && (
          <div style={{
            marginTop: 12, fontSize: 9, color: '#3a3020',
            letterSpacing: '0.2em', textTransform: 'uppercase', textAlign: 'center',
          }}>
            Tap to select
          </div>
        )}
      </div>
    </div>
  );
}

// ── Compare Screen ────────────────────────────────────────────────────────────
function CompareScreen({ comp, progress, pickCount, onChoose, onExit }) {
  const [choiceState, setChoiceState] = useState('idle');
  const [floats, setFloats] = useState([]);
  const [animKey, setAnimKey] = useState(0);

  const prevKey = useRef(null);
  useEffect(() => {
    const ck = comp ? `${comp.a.id}|${comp.b.id}` : null;
    if (ck !== prevKey.current) {
      setAnimKey(k => k + 1);
      setChoiceState('idle');
      prevKey.current = ck;
    }
  }, [comp]);

  const handleCard = useCallback((side, e) => {
    if (choiceState !== 'idle') return;
    setChoiceState(`chosen-${side}`);
    if (e) {
      const rect = e.currentTarget.getBoundingClientRect();
      const id = Date.now();
      setFloats(f => [...f, { id, x: rect.left + rect.width / 2, y: rect.top + 30, text: '◇' }]);
    }
    setTimeout(() => onChoose(side), 400);
  }, [choiceState, onChoose]);

  const handleTie = useCallback(() => {
    if (choiceState !== 'idle') return;
    setChoiceState('tie');
    setTimeout(() => onChoose('tie'), 400);
  }, [choiceState, onChoose]);

  if (!comp) return null;

  const stateA = choiceState === 'idle' ? 'idle'
    : choiceState === 'chosen-a' ? 'chosen'
    : choiceState === 'tie' ? 'tie'
    : 'rejected';

  const stateB = choiceState === 'idle' ? 'idle'
    : choiceState === 'chosen-b' ? 'chosen'
    : choiceState === 'tie' ? 'tie'
    : 'rejected';

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 16px 40px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 20, fontWeight: 300, letterSpacing: '0.1em', color: '#e8e0d0',
          }}>
            Comparison {pickCount}
          </div>
          <div style={{ fontSize: 10, color: '#4a3e30', letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 2 }}>
            {Math.round(progress * 100)}% complete
          </div>
        </div>
        <button
          className="reina-btn reina-btn-ghost"
          onClick={onExit}
          style={{ padding: '8px 16px', fontSize: 10 }}
        >
          Exit
        </button>
      </div>

      {/* Progress bar */}
      <div className="reina-progress-bar">
        <div className="reina-progress-fill" style={{ width: `${progress * 100}%` }} />
      </div>

      {/* Cards */}
      <div key={animKey} style={{ display: 'flex', gap: 8, alignItems: 'stretch', position: 'relative' }}>
        <CompareCard item={comp.a} side="a" cardState={stateA} onChoose={(e) => handleCard('a', e)} />
        <div style={{
          position: 'absolute', left: '50%', top: 12,
          transform: 'translateX(-50%)', zIndex: 10, pointerEvents: 'none',
        }}>
          <div className="reina-vs">vs</div>
        </div>
        <CompareCard item={comp.b} side="b" cardState={stateB} onChoose={(e) => handleCard('b', e)} />
      </div>

      {/* Tie button */}
      <div style={{ textAlign: 'center' }}>
        <button
          className="reina-btn reina-btn-ghost"
          onClick={handleTie}
          disabled={choiceState !== 'idle'}
          style={{
            padding: '10px 32px', fontSize: 10,
            opacity: choiceState !== 'idle' ? 0.4 : 1,
          }}
        >
          Tie — I like them equally
        </button>
      </div>

      <div style={{ height: 1, background: '#1a1710' }} />
      <p style={{ textAlign: 'center', color: '#3a3020', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', margin: 0 }}>
        Tap a piece to select it, or mark as a tie
      </p>

      {floats.map(f => (
        <FloatLabel
          key={f.id}
          text={f.text}
          x={f.x}
          y={f.y}
          onDone={() => setFloats(prev => prev.filter(ff => ff.id !== f.id))}
        />
      ))}
    </div>
  );
}

// ── Share Modal ───────────────────────────────────────────────────────────────
function ShareModal({ champions, fullItems, onClose }) {
  const [copied, setCopied] = useState(false);
  const isFull = !!fullItems;
  const isTied = champions.length > 1;

  const displayItems = isFull ? fullItems.slice(0, 3) : champions;

  const rankLabel = (item, i) => {
    if (!isFull) return isTied ? `No. ${i + 1} (Tied)` : '◇ Winner';
    const tied = fullItems.filter(x => x.displayRank === item.displayRank).length > 1;
    return `#${item.displayRank}${tied ? ' (Tied)' : ''}`;
  };

  const lines = isFull
    ? [
        'My Top 3:',
        ...displayItems.map(item => `${item.displayRank === 1 ? '◇' : '·'} #${item.displayRank} ${item.name} – ${item.brand}`),
        '',
        'reilly.fun/rank/reina',
      ]
    : [
        isTied ? 'My Tied Top Picks:' : 'My Top Pick:',
        ...champions.map(c => `◇ ${c.name} – ${c.brand}`),
        '',
        'reilly.fun/rank/reina',
      ];
  const text = lines.join('\n');

  const doShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: isFull ? 'My Top 3' : 'My Top Pick', text });
      } else {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (_) {}
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
    >
      <div
        className="reina-pop"
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0f0f0f', border: '1px solid #2a2520',
          padding: '28px', maxWidth: 400, width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
        }}
      >
        <div style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 24, fontWeight: 300, letterSpacing: '0.1em',
          color: '#e8e0d0', marginBottom: 6, textAlign: 'center',
        }}>
          {isFull ? 'Top 3' : isTied ? 'Top Picks' : 'Top Pick'}
        </div>
        <div style={{ height: 1, background: '#2a2520', margin: '12px 0 20px' }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
          {displayItems.map((item, i) => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 60, height: 60,
                background: 'radial-gradient(ellipse at center, #1e1c18 0%, #0d0b09 100%)',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.7)',
                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                <img
                  src={`/necklaces/${item.imageFile}`}
                  alt={item.name}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c5a96a', marginBottom: 3 }}>
                  {rankLabel(item, i)}
                </div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, color: '#e8e0d0' }}>{item.name}</div>
                <div style={{ fontSize: 11, color: '#6a5e50' }}>{item.material}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="reina-btn reina-btn-gold" onClick={doShare} style={{ padding: '13px 0', fontSize: 10, width: '100%' }}>
            {copied ? 'Copied' : navigator.share ? 'Share' : 'Copy to Clipboard'}
          </button>
          <button className="reina-btn reina-btn-ghost" onClick={onClose} style={{ padding: '11px 0', fontSize: 10, width: '100%' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Winner Screen ─────────────────────────────────────────────────────────────
function WinnerScreen({ champions, onContinue, onRestart, totalComparisons }) {
  const [showShare, setShowShare] = useState(false);
  const isTied = champions.length > 1;

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '32px 20px 60px',
    }}>
      <div style={{ maxWidth: 480, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            fontSize: 10, letterSpacing: '0.35em', textTransform: 'uppercase',
            color: '#c5a96a', marginBottom: 14,
          }}>
            {isTied ? 'Your Top Picks' : 'Your Top Pick'}
          </div>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(28px, 6vw, 42px)',
            fontWeight: 300, letterSpacing: '0.08em',
            color: '#e8e0d0', lineHeight: 1.1, marginBottom: 8,
          }}>
            {isTied ? 'A Tie at the Top' : 'Selected'}
          </div>
          <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, #3a3020, transparent)', margin: '16px 0' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
          {champions.map((item, i) => (
            <div
              key={item.id}
              className="reina-pop"
              style={{
                border: '1px solid #c5a96a',
                background: '#0f0f0f',
                overflow: 'hidden',
                position: 'relative',
                animationDelay: `${i * 0.08}s`,
              }}
            >
              {i === 0 && <div className="reina-shimmer" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }} />}
              <NecklaceImage item={item} height={isTied ? 160 : 220} />
              <div style={{ padding: '16px 18px 20px', borderTop: '1px solid #2a2520', position: 'relative', zIndex: 2 }}>
                <div style={{ fontSize: 9, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#c5a96a', marginBottom: 6 }}>
                  {isTied ? 'Tied #1' : '#1 Pick'}
                </div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 400, color: '#e8e0d0', marginBottom: 5 }}>
                  {item.name}
                </div>
                <div style={{ fontSize: 12, color: '#6a5e50', lineHeight: 1.5 }}>{item.material}</div>
                {item.detail && <div style={{ fontSize: 10, color: '#4a3e30', marginTop: 3 }}>{item.detail}</div>}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            className="reina-btn reina-btn-outline-gold"
            onClick={() => setShowShare(true)}
            style={{ padding: '14px 0', fontSize: 10, width: '100%' }}
          >
            Share My Pick
          </button>
          <button
            className="reina-btn reina-btn-gold"
            onClick={onContinue}
            style={{ padding: '14px 0', fontSize: 10, width: '100%' }}
          >
            Continue to Full Ranking
          </button>
          <button
            className="reina-btn reina-btn-ghost"
            onClick={onRestart}
            style={{ padding: '11px 0', fontSize: 10, width: '100%' }}
          >
            Start Over
          </button>
        </div>
      </div>

      {showShare && (
        <ShareModal champions={champions} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}

// ── Results Screen ────────────────────────────────────────────────────────────
function ResultsScreen({ items, comparisons, onRestart }) {
  const [showShare, setShowShare] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const champions = items.filter(i => i.displayRank === 1);
  const visible = showAll ? items : items.slice(0, 9);


  return (
    <div style={{ minHeight: '100dvh', padding: '0 0 60px' }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        padding: '40px 20px 28px',
        borderBottom: '1px solid #1a1710',
      }}>
        <div style={{
          fontSize: 9, letterSpacing: '0.4em', textTransform: 'uppercase',
          color: '#c5a96a', marginBottom: 12,
        }}>
          Final Ranking
        </div>
        <div style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(26px, 5vw, 38px)',
          fontWeight: 300, letterSpacing: '0.08em', color: '#e8e0d0',
        }}>
          Your Selection
        </div>
        <div style={{ fontSize: 10, color: '#3a3020', marginTop: 8, letterSpacing: '0.1em' }}>
          {comparisons} comparisons · {items.length} pieces ranked
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '28px 16px 0' }}>
        {/* Ranked list */}
        <div style={{ border: '1px solid #1e1b16', marginBottom: 16 }}>
          {visible.map((item, i) => {
            const isFirst = item.displayRank === 1;
            const isTied = items.filter(x => x.displayRank === item.displayRank).length > 1;
            return (
              <div
                key={item.id}
                className="reina-result-row"
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px',
                  borderBottom: i < visible.length - 1 ? '1px solid #1a1710' : 'none',
                  background: isFirst ? 'rgba(197,169,106,0.04)' : 'transparent',
                  animationDelay: `${Math.min(i, 9) * 0.04}s`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {isFirst && i === 0 && (
                  <div className="reina-shimmer" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
                )}

                {/* Rank number */}
                <div style={{
                  width: 28, textAlign: 'center', flexShrink: 0,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: isFirst ? 18 : 14,
                  color: isFirst ? '#c5a96a' : '#3a3020',
                  fontWeight: 400,
                  position: 'relative', zIndex: 1,
                }}>
                  {item.displayRank}
                </div>

                {/* Thumbnail */}
                <div style={{
                  width: 44, height: 44, flexShrink: 0,
                  background: 'radial-gradient(ellipse at center, #1a1810 0%, #0d0b09 100%)',
                  boxShadow: 'inset 0 0 12px rgba(0,0,0,0.7)',
                  overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative', zIndex: 1,
                }}>
                  <img
                    src={`/necklaces/${item.imageFile}`}
                    alt={item.name}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '3px', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Name + detail */}
                <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
                  <div style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 'clamp(12px, 2.5vw, 15px)',
                    color: isFirst ? '#e8e0d0' : '#8a7c6c',
                    fontWeight: 400, lineHeight: 1.3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.name}
                    {isTied && <span style={{ fontSize: 9, color: '#c5a96a', marginLeft: 6, letterSpacing: '0.15em' }}>TIED</span>}
                  </div>
                  <div style={{ fontSize: 10, color: '#3a3020', lineHeight: 1.4, marginTop: 2 }}>
                    {item.material}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {items.length > 9 && (
          <button
            className="reina-btn reina-btn-ghost"
            onClick={() => setShowAll(v => !v)}
            style={{ width: '100%', padding: '10px 0', fontSize: 10, marginBottom: 12 }}
          >
            {showAll ? 'Show Less' : `Show All ${items.length} Pieces`}
          </button>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <button
            className="reina-btn reina-btn-outline-gold"
            onClick={() => setShowShare(true)}
            style={{ padding: '14px 0', fontSize: 10, width: '100%' }}
          >
            Share My Top Pick
          </button>
          <button
            className="reina-btn reina-btn-ghost"
            onClick={onRestart}
            style={{ padding: '12px 0', fontSize: 10, width: '100%' }}
          >
            Start Over
          </button>
        </div>
      </div>

      {showShare && (
        <ShareModal champions={champions} fullItems={items} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ReinaRank() {
  const [phase, setPhase] = useState('intro');
  const [findTopState, setFindTopState] = useState(null);
  const [sortState, setSortState] = useState(null);
  const loggedRef = useRef({ winner: false, full: false });

  useImagePreloader();

  const handleStart = useCallback(() => {
    setFindTopState(initFindTop(ACTIVE_NECKLACES));
    setSortState(null);
    loggedRef.current = { winner: false, full: false };
    setPhase('findTop');
  }, []);

  const handleFindTopChoose = useCallback((choice) => {
    setFindTopState(prev => applyFindTopChoice(prev, choice));
  }, []);

  const handleSortChoose = useCallback((choice) => {
    setSortState(prev => applyGroupChoice(prev, choice));
  }, []);

  // FindTop done → winner screen
  useEffect(() => {
    if (findTopState?.done && phase === 'findTop') {
      const t = setTimeout(() => setPhase('winner'), 300);
      return () => clearTimeout(t);
    }
  }, [findTopState?.done, phase]);

  // GroupSort done → results screen
  useEffect(() => {
    if (sortState?.done && phase === 'fullSort') {
      const t = setTimeout(() => setPhase('results'), 300);
      return () => clearTimeout(t);
    }
  }, [sortState?.done, phase]);

  // Log winner
  useEffect(() => {
    if (phase === 'winner' && findTopState?.done && !loggedRef.current.winner) {
      loggedRef.current.winner = true;
      logNecklaceResult({
        type: 'top_pick',
        champions: findTopState.champions,
        fullRanking: null,
        comparisons: findTopState.comparisons,
      });
    }
  }, [phase, findTopState]);

  // Log full results
  const fullResults = useMemo(() => {
    if (!findTopState?.done) return null;
    if (!sortState?.done) return null;
    return flattenRanked(findTopState.champions, sortState);
  }, [findTopState, sortState]);

  useEffect(() => {
    if (phase === 'results' && fullResults && !loggedRef.current.full) {
      loggedRef.current.full = true;
      const totalComparisons = (findTopState?.comparisons ?? 0) + (sortState?.comparisons ?? 0);
      logNecklaceResult({
        type: 'full_ranking',
        champions: findTopState.champions,
        fullRanking: fullResults,
        comparisons: totalComparisons,
      });
    }
  }, [phase, fullResults, findTopState, sortState]);

  const handleContinue = useCallback(() => {
    const losers = findTopState?.losers ?? [];
    if (losers.length === 0) {
      setSortState({ sorted: [], done: true, comparisons: 0 });
      setPhase('results');
    } else {
      setSortState(initGroupSort(losers));
      setPhase('fullSort');
    }
  }, [findTopState]);

  const handleRestart = useCallback(() => {
    setFindTopState(null);
    setSortState(null);
    setPhase('intro');
  }, []);

  // What to pass to CompareScreen
  const compareProps = useMemo(() => {
    if (phase === 'findTop' && findTopState && !findTopState.done) {
      const comp = getFindTopComp(findTopState);
      const progress = findTopState.comparisons / (findTopState.totalItems - 1);
      return { comp, progress, pickCount: findTopState.comparisons + 1, onChoose: handleFindTopChoose };
    }
    if (phase === 'fullSort' && sortState && !sortState.done) {
      const comp = getGroupComp(sortState);
      const progress = getGroupSortProgress(sortState);
      return { comp, progress, pickCount: (findTopState?.comparisons ?? 0) + sortState.comparisons + 1, onChoose: handleSortChoose };
    }
    return null;
  }, [phase, findTopState, sortState, handleFindTopChoose, handleSortChoose]);

  const totalComparisons = (findTopState?.comparisons ?? 0) + (sortState?.comparisons ?? 0);

  return (
    <div className="reina-velvet-bg">
      {phase === 'intro' && <IntroScreen onStart={handleStart} />}

      {(phase === 'findTop' || phase === 'fullSort') && compareProps && (
        <CompareScreen
          comp={compareProps.comp}
          progress={compareProps.progress}
          pickCount={compareProps.pickCount}
          onChoose={compareProps.onChoose}
          onExit={handleRestart}
        />
      )}

      {phase === 'winner' && findTopState && (
        <WinnerScreen
          champions={findTopState.champions}
          onContinue={handleContinue}
          onRestart={handleRestart}
          totalComparisons={findTopState.comparisons}
        />
      )}

      {phase === 'results' && fullResults && (
        <ResultsScreen
          items={fullResults}
          comparisons={totalComparisons}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}
